import { PossibleChartType, ResourceObject } from './document/ResourceObject';
import { RelationshipObject } from './document/RelationshipObject';
import { MetaObject } from './document/MetaObject';
import { Typed, TypedAttributes } from './TypedAttributes';
import { AttributeType } from './document/AttributeType';
import { CreateOrUpdateObject } from './document/CreateOrUpdateObject';
import { JsonDocument } from './JsonDocument';

export type ResourceMeta = MetaObject & {
    columns?: Record<string, Typed> | Record<string, string>,
    calculator?: {
        view: string;
    }
};

export class Resource<T> {
    private readonly resource: ResourceObject<T>;
    private document?: JsonDocument<T>;

    public constructor(resource: ResourceObject<T>, document?: JsonDocument<T>) {
        this.resource = resource;
        this.document = document;
    }

    public setDocument(document: JsonDocument<T>): Resource<T> {
        this.document = document;

        return this;
    }

    public getAttributes(): TypedAttributes<T> & { [key: string]: any } & PossibleChartType {
        return this.resource.attributes;
    }

    public getAttribute<U extends keyof T>(attribute: U): T[U] {
        const attr = this.getAttributes()[attribute];

        if (attr && Object.prototype.hasOwnProperty.call(attr, 'type')) {
            return (attr as Typed).value;
        }

        return attr as T[U];
    }

    public getAttributeType(attribute: keyof T): AttributeType | undefined {
        const attr = this.getAttributes()[attribute];

        if (Object.prototype.hasOwnProperty.call(attr, 'type')) {
            return (attr as Typed).type;
        }

        return undefined;
    }

    public getRelationships(): RelationshipObject | undefined {
        return this.resource.relationships;
    }

    public getRelationsByType<U>(type: string): Array<Resource<U>> {
        if (!this.document) {
            throw new Error('Can not fetch relationships when no document is defined');
        }

        const relationships = this.getRelationships();

        if (
            !relationships
            || !Object.prototype.hasOwnProperty.call(relationships, type)
            || !relationships[type].data
        ) {
            return [];
        }

        const includedRelations: Array<Resource<U>> = [];

        if (Array.isArray(relationships[type].data)) {
            (relationships[type].data as ResourceObject<T>[]).forEach((resource) => {
                if (resource.type === this.getType()) {
                    const resourceInDocument = this.document!.getResource(resource.type, resource.id);

                    if (resourceInDocument) {
                        includedRelations.push(new Resource<U>(
                            resourceInDocument.getResourceObject() as ResourceObject<U>
                        ));
                        return;
                    }
                }

                this.document!.getIncludes().forEach((include: ResourceObject<any>) => {
                    if (resource.type === include.type && resource.id === include.id) {
                        includedRelations.push(new Resource<U>(include));
                    }
                });
            });
        } else {
            this.document!.getIncludes().forEach((include: ResourceObject<any>) => {
                if (
                    (relationships[type].data as ResourceObject<T>).type === include.type
                    && (relationships[type].data as ResourceObject<T>).id === include.id
                ) {
                    includedRelations.push(new Resource<U>(include));
                }
            });
        }

        return includedRelations;
    }

    public getMeta(): ResourceMeta | undefined {
        return this.resource.meta;
    }

    public getColumns(): Record<string, Typed> | Record<string, string> {
        const meta = this.getMeta();

        return meta?.columns || {};
    }

    public getId(): string {
        return this.resource.id;
    }

    public getType(): string {
        return this.resource.type;
    }

    public makeUpdateObject(data: Partial<T> & Record<string, any>): CreateOrUpdateObject<T> {
        const updatedValues: Partial<T> = {};
        const attributes = this.getAttributes();

        Object.keys(data).forEach((attribute: string) => {
            if ((Object.prototype.hasOwnProperty.call(attributes, attribute)
                && this.getAttribute(attribute as keyof T) !== data[attribute as keyof T])
                || (!Object.prototype.hasOwnProperty.call(attributes, attribute)
                && !!data[attribute])
            ) {
                updatedValues[attribute as keyof T] = data[attribute as keyof T];
            }
        });

        return {
            data: {
                type: this.getType(),
                id: this.getId(),
                attributes: updatedValues
            }
        };
    }

    public dirtyAttributes(newAttributes: Partial<T>): (keyof T)[] {
        const dirtyAttributes: (keyof T)[] = [];

        Object.keys(newAttributes).forEach((attribute: string) => {
            // handle empty and null as same
            // @ts-ignore
            if (this.getAttribute(attribute) === null && newAttributes[attribute] === '') {
                return;
            }

            // @ts-ignore
            if (this.getAttribute(attribute) !== newAttributes[attribute]) {
                dirtyAttributes.push(attribute as keyof T);
            }
        });

        return dirtyAttributes;
    }

    public static makeCreateObject<U>(
        type: string,
        data: U | U[]
    ): CreateOrUpdateObject<U> {
        if (Array.isArray(data)) {
            return {
                data: {
                    type,
                    records: data
                }
            };
        }

        return {
            data: {
                type,
                attributes: data
            }
        };
    }

    public getResourceObject<U = T>(): ResourceObject<U> {
        return this.resource as unknown as ResourceObject<U>;
    }
}
